import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { showToast } from '@klpkonsern/ui';

import type { ProjectTypeStatus } from '@klp/prosjekt/interfaces';
import { ProsjektRoutes } from '@klp/prosjekt/config';

import { useExecuteProsjektTaskMutation } from '../services/rtkApi';
import { CustomFormField } from './FormField';
import type { Aktivitet, FormField } from '../interfaces';
import {
    findCamundaPropertyValue,
    formfieldIsHidden,
    formfieldIsRequired,
    formfieldIsReadOnly,
    withTextMaxLength,
} from '../utils';
import { AutoSaveFormfields } from './AutoSaveProsjektForm';
import type { Leverandor } from './LeverandorListSelector';
import { DynamicAssignSelector } from './DynamicAssignSelector';
import { FormContainerFieldGodkjenn } from './FormContainerFieldGodkjenn';
import { FormContainerFieldComplete } from './FormContainerFieldComplete';
import { useFormContext } from '../context/FormContext';
import { ScrollToFirstError } from './ScrollToFirstError';

const ASSIGNMENT_ERROR_REGEX =
    /Oppgave\/task Id \(([^)]+)\) is assigned to \(([^)]+)\) not to user \(([^)]+)\)/;

export const createValidationSchema = (
    formFields: Array<FormField>,
    values: FormValuesType,
    regionContext?: string,
    t?: (key: string, options?: Record<string, unknown>) => string,
    // Autosave validates without required rules so drafts can still be saved
    options?: { skipRequired?: boolean },
) => {
    const schemaFields: { [fieldId: string]: Yup.AnySchema } = {};

    formFields.forEach((field) => {
        if (!field.id) return;

        const properties = field.properties ?? [];

        const isReadOnly = formfieldIsReadOnly(field, regionContext);
        const subtype = findCamundaPropertyValue('type', properties);
        // dynamicAssign is marked hidden in BPMN so the generic renderer skips
        // it, but DynamicAssignSelector renders it, so it must be validated
        const isHidden =
            subtype !== 'dynamicAssign' &&
            formfieldIsHidden(field, values, regionContext);

        if (isReadOnly || subtype === 'constant' || isHidden) {
            return;
        }

        const required = formfieldIsRequired(field, regionContext);
        const shouldBeRequired = required && !options?.skipRequired;
        if (field.type === 'string') {
            let validator = Yup.string();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );

                if (field.id === 'vurderingsskjema') {
                    validator = validator.test(
                        'is-greater-than-zero',
                        t?.('project.errors.invalidTotalVurderingsskjema') ??
                            'Required',
                        (value) => {
                            if (value === undefined || value === '') {
                                return false;
                            }
                            const parsedValue = parseFloat(value);
                            return parsedValue > 0;
                        },
                    );
                }
            }

            validator = withTextMaxLength(validator, subtype, t);

            if (
                field.id === 'prosjektNummerD365' ||
                field.id === 'tiltakNummerD365'
            ) {
                validator = validator.matches(
                    new RegExp('^([NDSE]{1}\\d{3}(\\.\\d{0,4})*)$'),
                    t?.('project.errors.invalidProjectNumber') ??
                        'Invalid project number',
                );
            }
            if (
                field.id === 'entreprenorListe' ||
                field.id === 'leverandorListe'
            ) {
                validator = validator.test(
                    'json',
                    t?.('project.errors.companyNotSelected') ??
                        'Company not selected',
                    (value) => {
                        try {
                            const virksomhetList = JSON.parse(
                                value!,
                            ) as Array<Leverandor>;

                            return (
                                (!shouldBeRequired &&
                                    virksomhetList.length === 0) ||
                                virksomhetList.every(
                                    (v) => v.organisasjonsnummer !== undefined,
                                )
                            );
                        } catch (error) {
                            return false;
                        }
                    },
                );
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'long') {
            let validator = Yup.number();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'date') {
            let validator = Yup.date();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'boolean') {
            let validator = Yup.boolean().nullable();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );
            }

            schemaFields[field.id] = validator;
        }

        if (field.type === 'enum') {
            let validator = Yup.string();

            if (shouldBeRequired) {
                validator = validator.required(
                    t?.('formGenerator.fieldRequired') ??
                        'This field is required',
                );
            }

            schemaFields[field.id] = validator;
        }
    });

    return Yup.object().shape(schemaFields);
};

export type FormValuesType = { [id: string]: string };

type FormContainerProps = {
    prosessInstansId: string;
    prosjektId: string;
    activity: Aktivitet;
    formFields: Array<FormField>;
    prosjektTypeStatus: ProjectTypeStatus;
    isDisabled: boolean;
};

export const FormContainer = ({
    prosessInstansId,
    prosjektId,
    activity,
    formFields,
    prosjektTypeStatus,
    isDisabled,
}: FormContainerProps) => {
    const { t, i18n } = useTranslation();
    const [executeProsjektTask, { isLoading }] =
        useExecuteProsjektTaskMutation();
    const [approvalStatus, setApprovalStatus] = useState('');
    const { regionContext } = useFormContext();
    const history = useHistory();

    const initialValues = useMemo(
        () =>
            Object.fromEntries(
                formFields.map((field) => [field.id, field.value ?? '']),
            ),
        [formFields],
    );

    // Memoize the validation schema to prevent recreation on every render
    const validationSchema = useMemo(
        () =>
            Yup.lazy((values: FormValuesType) =>
                createValidationSchema(formFields, values, regionContext, t),
            ),
        [formFields, regionContext, t],
    );

    const autosaveValidationSchema = useMemo(
        () =>
            Yup.lazy((values: FormValuesType) =>
                createValidationSchema(formFields, values, regionContext, t, {
                    skipRequired: true,
                }),
            ),
        [formFields, regionContext, t],
    );

    const isFieldTypeGodkjenn = useMemo(
        () => formFields.find((f) => f.id.includes('Godkjent'))?.id,
        [formFields],
    );

    const dynamicAssignField = useMemo(
        () =>
            formFields.find(
                (f) =>
                    findCamundaPropertyValue('type', f.properties) ===
                    'dynamicAssign',
            ),
        [formFields],
    );

    const handleSubmit = useCallback(
        (values: FormValuesType) => {
            if (!activity.id) return;

            const updatedFormFields: Array<FormField> = formFields.map(
                (field) => {
                    const value =
                        isFieldTypeGodkjenn === field.id
                            ? approvalStatus
                            : values[field.id];

                    return { ...field, value };
                },
            );

            executeProsjektTask({
                oppgaveId: activity.id,
                formFields: updatedFormFields,
            })
                .unwrap()
                .then(() => {
                    showToast({
                        body: t('prosjekt.successfulOperation'),
                        type: 'success',
                    });
                    history.push(ProsjektRoutes.projectSchema());
                })
                .catch((e) => {
                    const match = e?.data?.match(ASSIGNMENT_ERROR_REGEX);
                    showToast({
                        body: match
                            ? t('prosjekt.notAssignedToUser')
                            : t('general.error.message'),
                        type: 'error',
                    });
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            activity.id,
            formFields,
            isFieldTypeGodkjenn,
            approvalStatus,
            executeProsjektTask,
            history,
        ],
    );

    // Memoize the formFields array
    const memoizedFormFields = useMemo(
        () =>
            activity.formFields.filter((f) => f.id !== dynamicAssignField?.id),
        [activity, dynamicAssignField],
    );

    // Same order as rendered: DynamicAssignSelector comes after the fields
    const scrollableFormFields = useMemo(
        () =>
            dynamicAssignField
                ? [...memoizedFormFields, dynamicAssignField]
                : memoizedFormFields,
        [memoizedFormFields, dynamicAssignField],
    );

    return (
        <Formik
            name="formContainer"
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize={true}
        >
            {({ values, isSubmitting }) => (
                <Form noValidate={true}>
                    <ScrollToFirstError formFields={scrollableFormFields} />
                    <AutoSaveFormfields
                        validationSchema={autosaveValidationSchema}
                        fields={formFields}
                        activityId={activity.id}
                        isDisabled={isDisabled || isSubmitting}
                    />
                    {memoizedFormFields.map((field, index) => (
                        <CustomFormField
                            containerModule="prosjekt"
                            key={`${field.id}-${index}`}
                            prosessInstansId={prosessInstansId}
                            prosjektId={prosjektId}
                            field={field}
                            fields={memoizedFormFields}
                            prosjektTypeStatus={prosjektTypeStatus}
                            formIsDisabled={isDisabled}
                            locale={i18n.language}
                            hiddenField={formfieldIsHidden(
                                field,
                                values,
                                regionContext,
                            )}
                        />
                    ))}

                    {dynamicAssignField && activity.id && (
                        <DynamicAssignSelector
                            field={dynamicAssignField}
                            isReadOnly={isDisabled}
                            activityId={activity.id}
                            values={values}
                        />
                    )}

                    {isFieldTypeGodkjenn && (
                        <FormContainerFieldGodkjenn
                            isDisabled={isDisabled}
                            isLoading={isLoading}
                            setApprovalStatus={setApprovalStatus}
                            onSubmit={handleSubmit}
                        />
                    )}
                    {!isFieldTypeGodkjenn && (
                        <FormContainerFieldComplete
                            isDisabled={isDisabled}
                            isLoading={isLoading}
                            activity={activity}
                        />
                    )}
                </Form>
            )}
        </Formik>
    );
};
